import React, { useEffect, useMemo, useState } from 'react';
import { Link } from "react-router-dom";

import BasicCRUD from './../core/basic-crud';
import AppContext from "../../app-context";
import { socketUseEffect } from "../../socket";
import { useAuth } from "../../AuthContext";
import ProgressBar from "../page-elements/progress-bar";
import FadeAlert from "../core/fade-alert";

const getInitialLang = () => (localStorage.getItem("lang") || "en").toLowerCase();

// -------- i18n --------
const dict = {
    en: {
        // page
        title: "Broken Links",
        subTitle: "add / edit broken links",
        homeURL: "/dashboard",
        addTitle: "Add Broken Link",
        editTitle: "Edit Broken Link",

        // columns
        colStatus: "Status",
        colURL: "URL",
        colBrokenCount: "Broken Links Count",
        colValidatedAt: "Validated At",
        colNextUpdate: "Next Update",
        colLastUpdate: "Last Update",
        colContactGroup: "Contact Group",

        // form
        tabMain: "Main",
        fieldProtocol: "Protocol",
        fieldURL: "URL",
        fieldContactGroup: "Contact Group",
        basicAuth: "Basic Authorization",
        login: "Login",
        password: "Password",
        validateAt: "Validated At",
        dayOfWeek: "Day of Week",
        dayOfMonth: "Day of Month",
        time: "Time",

        // selects
        choose: " -- choose -- ",
        yes: "Yes",
        no: "No",
        protocols: { http: "HTTP", https: "HTTPS" },
        schedule: { daily: "Daily", weekly: "Weekly", monthly: "Monthly" },
        weekdays: {
            monday: "Monday",
            tuesday: "Tuesday",
            wednesday: "Wednesday",
            thursday: "Thursday",
            friday: "Friday",
            saturday: "Saturday",
            sunday: "Sunday",
        },

        // actions
        edit: "Edit",
        delete: "Delete",

        // quota/plan
        noResources: "You don't have any available resources. Please change your pricing plan to get more items.",
        planExpired: "Your pricing plan has expired. Please renew or upgrade your plan to continue accessing this feature.",

        // delete dialog
        delAlert: "Deleting a broken links validator cannot be undone.",
        delText: "To confirm deletion, enter the name of the broken links validator in the next input field",
        delTitle: 'Delete broken links validator: "{name}"',

        // toasts
        toastEntity: "Broken Link",
        createdOk: "created successfully",
        updatedOk: "updated successfully",
        deletedOk: "deleted successfully",
    },

    ru: {
        title: "Битые ссылки",
        subTitle: "добавление / редактирование проверок",
        homeURL: "/dashboard",
        addTitle: "Добавить проверку ссылок",
        editTitle: "Редактировать проверку ссылок",

        colStatus: "Статус",
        colURL: "URL",
        colBrokenCount: "Количество битых ссылок",
        colValidatedAt: "Периодичность",
        colNextUpdate: "Следующее обновление",
        colLastUpdate: "Последнее обновление",
        colContactGroup: "Группа контактов",

        tabMain: "Основное",
        fieldProtocol: "Протокол",
        fieldURL: "URL",
        fieldContactGroup: "Группа контактов",
        basicAuth: "Базовая авторизация",
        login: "Логин",
        password: "Пароль",
        validateAt: "Проверять",
        dayOfWeek: "День недели",
        dayOfMonth: "День месяца",
        time: "Время",

        choose: " -- выберите -- ",
        yes: "Да",
        no: "Нет",
        protocols: { http: "HTTP", https: "HTTPS" },
        schedule: { daily: "Ежедневно", weekly: "Еженедельно", monthly: "Ежемесячно" },
        weekdays: {
            monday: "Понедельник",
            tuesday: "Вторник",
            wednesday: "Среда",
            thursday: "Четверг",
            friday: "Пятница",
            saturday: "Суббота",
            sunday: "Воскресенье",
        },

        edit: "Редактировать",
        delete: "Удалить",

        noResources: "У вас нет доступных ресурсов. Пожалуйста, измените тарифный план, чтобы получить больше элементов.",
        planExpired: "Срок действия вашего тарифного плана истёк. Обновите или продлите план, чтобы продолжить пользоваться функцией.",

        delAlert: "Удаление валидатора битых ссылок необратимо.",
        delText: "Чтобы подтвердить удаление, введите имя валидатора в следующее поле ввода",
        delTitle: 'Удалить валидатор битых ссылок: «{name}»',

        toastEntity: "Проверка ссылок",
        createdOk: "успешно создана",
        updatedOk: "успешно обновлена",
        deletedOk: "успешно удалена",
    },

    ua: {
        title: "Биті посилання",
        subTitle: "додавання / редагування перевірок",
        homeURL: "/dashboard",
        addTitle: "Додати перевірку посилань",
        editTitle: "Редагувати перевірку посилань",

        colStatus: "Статус",
        colURL: "URL",
        colBrokenCount: "Кількість битих посилань",
        colValidatedAt: "Періодичність",
        colNextUpdate: "Наступне оновлення",
        colLastUpdate: "Останнє оновлення",
        colContactGroup: "Група контактів",

        tabMain: "Основне",
        fieldProtocol: "Протокол",
        fieldURL: "URL",
        fieldContactGroup: "Група контактів",
        basicAuth: "Базова авторизація",
        login: "Логін",
        password: "Пароль",
        validateAt: "Перевіряти",
        dayOfWeek: "День тижня",
        dayOfMonth: "День місяця",
        time: "Час",

        choose: " -- оберіть -- ",
        yes: "Так",
        no: "Ні",
        protocols: { http: "HTTP", https: "HTTPS" },
        schedule: { daily: "Щодня", weekly: "Щотижня", monthly: "Щомісяця" },
        weekdays: {
            monday: "Понеділок",
            tuesday: "Вівторок",
            wednesday: "Середа",
            thursday: "Четвер",
            friday: "Пʼятниця",
            saturday: "Субота",
            sunday: "Неділя",
        },

        edit: "Редагувати",
        delete: "Видалити",

        noResources: "У вас немає доступних ресурсів. Будь ласка, змініть тарифний план, щоб отримати більше елементів.",
        planExpired: "Строк дії вашого тарифного плану минув. Поновіть або оновіть план, щоб продовжити користування функцією.",

        delAlert: "Видалення валідатора битих посилань неможливо скасувати.",
        delText: "Щоб підтвердити видалення, введіть назву валідатора у наступне поле вводу",
        delTitle: 'Видалити валідатор битих посилань: «{name}»',

        toastEntity: "Перевірка посилань",
        createdOk: "успішно створено",
        updatedOk: "успішно оновлено",
        deletedOk: "успішно видалено",
    },
};

const BrokenLinks = ({ lang: langProp, ...props }) => {
    const lang = (langProp || getInitialLang());
    const t = useMemo(() => dict[lang] || dict.en, [lang]);

    const object = "broken_links";
    const [response, setResponse] = useState({});
    const [fieldsData, setFieldsData] = useState({});

    const [totalData, setTotalData] = useState([]);
    const [contacts, setContacts] = useState([{ id: 0, name: t.choose }]);

    const { user } = useAuth();
    const listeners = [
        { name: "contacts", fn: "items-list", args: { user_id: user.user_id, site_id: props.site_id }, saveDataFN: setContacts },
        { name: "dashboard", fn: "reports-total", args: {}, saveDataFN: setTotalData },
    ];

    // Обновляем лимиты после create/update/delete
    useEffect(() => socketUseEffect(listeners, user), [user, props.site_id, response?.status, response?.created, response?.updated, response?.delete]);

    const formatShowURL = (data) => (
        <Link to={`/broken-link/${data.id}/dashboard`} className="sidebar-link" data-text="Server Details">
            {data.url}
        </Link>
    );

    const columns = [
        { label: t.colStatus, key: "validate_status" },
        { label: t.colURL, key: "url", format: formatShowURL },
        { label: t.colBrokenCount, key: "last_broken_links_count" },
        { label: t.colValidatedAt, key: "validate_at" },
        { label: t.colNextUpdate, key: "next_update" },
        { label: t.colLastUpdate, key: "last_update" },
        { label: t.colContactGroup, key: "contacts_group_name" },
    ];

    const showField = (data, field) => {
        const key = field.key;
        const updatedField = { ...field };
        if ((key === "basic_auth_username" || key === "basic_auth_password") && String(data['basic_auth']) === "0") {
            updatedField.readonly = true;
        }
        return updatedField;
    };

    const showRule = (data, key) => {
        if ((key === "validate_day_of_week" || key === "validate_day_of_month") && data['validate_at'] === "daily") {
            return false;
        }
        if (key === "validate_day_of_month" && data['validate_at'] === "weekly") {
            return false;
        }
        if (key === "validate_day_of_week" && data['validate_at'] === "monthly") {
            return false;
        }
        return true;
    };

    const daysOfMonth = Array.from({ length: 31 }, (_, i) => ({ id: i + 1, name: String(i + 1) }));
    const weekdays = [
        { id: "monday", name: t.weekdays.monday },
        { id: "tuesday", name: t.weekdays.tuesday },
        { id: "wednesday", name: t.weekdays.wednesday },
        { id: "thursday", name: t.weekdays.thursday },
        { id: "friday", name: t.weekdays.friday },
        { id: "saturday", name: t.weekdays.saturday },
        { id: "sunday", name: t.weekdays.sunday },
    ];
    const schedule = [
        { id: "daily", name: t.schedule.daily },
        { id: "weekly", name: t.schedule.weekly },
        { id: "monthly", name: t.schedule.monthly },
    ];
    const protocols = [
        { id: "http", name: t.protocols.http },
        { id: "https", name: t.protocols.https },
    ];

    const fields = [
        {
            type: "tab",
            name: t.tabMain,
            image: "",
            showField,
            showRule,
            fields: [
                [
                    { label: t.fieldProtocol, key: "protocol", default_value: "https", type: "selectbox", data: protocols, showLabel: "top" },
                    { label: t.fieldURL, key: "url", default_value: "", showLabel: "top" },
                    { label: t.fieldContactGroup, key: "contact_group_id", default_value: "0", type: "selectbox", data: contacts, showLabel: "top" },
                ],
                [
                    { label: t.basicAuth, key: "basic_auth", type: "selectbox", data: [{ id: '1', name: t.yes }, { id: '0', name: t.no }], default_value: "0", showLabel: "top" },
                    { label: t.login, key: "basic_auth_username", default_value: "", showLabel: "top" },
                    { label: t.password, key: "basic_auth_password", default_value: "", showLabel: "top" },
                ],
                [
                    { label: t.validateAt, key: "validate_at", default_value: "daily", type: "selectbox", data: schedule, showLabel: "top" },
                    { label: t.dayOfWeek, key: "validate_day_of_week", default_value: "monday", type: "selectbox", data: weekdays, showLabel: "top" },
                    { label: t.dayOfMonth, key: "validate_day_of_month", default_value: "1", type: "selectbox", data: daysOfMonth, showLabel: "top" },
                    { label: t.time, key: "validate_time", type: "time", default_value: "", showLabel: "top" },
                ],
            ],
        },
    ];

    const actions = [
        { name: "edit", label: t.edit, controller: "crud" },
        { name: "delete", label: t.delete, controller: "crud" },
    ];

    let filters = { site_id: props.site_id || null };

    const expired = user?.expired ?? 1;
    const disabled = expired
        ? { add: { disable: true, title: t.planExpired } }
        : {
            add: {
                disable: (totalData.brokenlinks - totalData.used_brokenlinks) < 1,
                title: t.noResources,
            },
        };

    return (
        <>
            <BasicCRUD
                singleArgs={props?.singleArgs ?? false}
                title={t.title}
                subTitle={t.subTitle}
                homeURL={t.homeURL}
                addTitle={t.addTitle}
                disable={disabled}
                editTitle={t.editTitle}
                object={object}
                columns={columns}
                fields={fields}
                actions={actions}
                fieldsData={fieldsData}
                setFieldsData={setFieldsData}
                response={response}
                setResponse={setResponse}
                ownHandler={true}
                deleteConfirmationDialog={{
                    alert: t.delAlert,
                    text: t.delText,
                    title: t.delTitle,
                    column: 'url',
                }}
            >
                {!props.singleArgs && (
                    <ProgressBar lang={lang} used={totalData.used_brokenlinks} allowed={totalData.brokenlinks} />
                )}

                {response.status && response.status === 1 ? (
                    <FadeAlert response={response} resetResponse={() => setResponse([])}>
                        {response.created && response.created === 1 ? (
                            <>
                                <strong>{t.toastEntity}</strong> {t.createdOk}
                            </>
                        ) : null}
                        {response.updated && response.updated === 1 ? (
                            <>
                                <strong>{t.toastEntity}</strong> {t.updatedOk}
                            </>
                        ) : null}
                        {response.delete && response.delete === 1 ? (
                            <>
                                <strong>{t.toastEntity}</strong> {t.deletedOk}
                            </>
                        ) : null}
                    </FadeAlert>
                ) : null}
            </BasicCRUD>
        </>
    );
};

export default BrokenLinks;
